import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Edit, Trash2, Eye, MoreVertical } from "lucide-react";
import toast from "react-hot-toast";

const BASE = "https://api.livestockify.com/api/urjabio";
const USERS_URL = `${BASE}/users`;
const PROJECTS_URL = `${BASE}/projects`;

const isAdminUser = (u) =>
  String(u.roleType || u.role || "").toUpperCase() === "ADMIN";

const AllUser = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(null);

  const getToken = () => localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      try {
        const token = getToken();
        if (!token) return;

        const authHeaders = {
          Authorization: `Bearer ${token}`,
        };

        const [userRes, projectRes] = await Promise.all([
          axios.get(USERS_URL, { headers: authHeaders }),
          axios.get(PROJECTS_URL, { headers: authHeaders }),
        ]);

        setUsers(userRes.data || []);
        setProjects(projectRes.data || []);
      } catch {
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const getUserProject = (user) =>
    projects.find(
      (p) => p.clientId === user.userUuid || p.clientId === user.clientId,
    );

  const toggleMenu = (id) => {
    setMenuOpen((prev) => (prev === id ? null : id));
  };

  const handleEditUser = (user) => {
    navigate("/urjabio/admin/add-user", {
      state: { editData: user },
    });
  };

  const handleDeleteUser = async (user) => {
    try {
      await axios.delete(`${USERS_URL}/${user.userUuid}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success("User deleted");
      setUsers((prev) => prev.filter((u) => u.userUuid !== user.userUuid));
    } catch {
      toast.error("Delete failed");
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    const nameMatch = `${u.userFirstname || ""} ${u.userLastname || ""} ${
      u.userEmail || ""
    }`
      .toLowerCase()
      .includes(q);

    if (isAdminUser(u)) return nameMatch;

    const project = getUserProject(u);
    const deviceMatch = project?.devices?.some((d) =>
      d.deviceId?.toLowerCase().includes(q),
    );

    return nameMatch || deviceMatch;
  });

  const adminUsers = filteredUsers.filter(isAdminUser);
  const clientUsers = filteredUsers.filter((u) => !isAdminUser(u));

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        Loading users...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <button
          onClick={() => navigate("/urjabio/admin/dashboard")}
          className="text-sm text-gray-600 hover:text-[#34C759]"
        >
          ← Go Back
        </button>

        <h2 className="text-2xl font-semibold">All Users</h2>

        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate("/urjabio/admin/add-user")}
            className="bg-[#34C759] text-white px-5 py-2 rounded-md w-full sm:w-auto text-center font-medium shadow active:scale-95"
          >
            Add User
          </button>

          <div className="relative w-full sm:w-64">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full border rounded-md px-4 py-2"
            />
            <Search
              className="absolute right-3 top-2.5 text-gray-400"
              size={18}
            />
          </div>
        </div>
      </div>

      {/* ================= ADMINS (details only) ================= */}
      <section className="mb-8">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-800">Admins</h3>
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
            {adminUsers.length}
          </span>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          Admins have access to all projects — no assigned project needed.
        </p>

        {adminUsers.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
            No admin users found.
          </div>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block border border-gray-300 rounded-md overflow-hidden">
              <div className="grid grid-cols-6 bg-gray-100 text-sm font-semibold text-gray-700">
                <div className="px-4 py-3 border-r">First Name</div>
                <div className="px-4 py-3 border-r">Last Name</div>
                <div className="px-4 py-3 border-r">Email</div>
                <div className="px-4 py-3 border-r">Phone</div>
                <div className="px-4 py-3 border-r text-center">Edit</div>
                <div className="px-4 py-3 text-center">Delete</div>
              </div>
              <div className="divide-y">
                {adminUsers.map((u) => (
                  <div
                    key={u.userUuid}
                    className="grid grid-cols-6 items-center bg-white"
                  >
                    <div className="px-4 py-3 border-r">
                      {u.userFirstname || "-"}
                    </div>
                    <div className="px-4 py-3 border-r">
                      {u.userLastname || "-"}
                    </div>
                    <div className="px-4 py-3 border-r text-sm break-all">
                      {u.userEmail || "-"}
                    </div>
                    <div className="px-4 py-3 border-r">
                      {u.userPhoneno || "-"}
                    </div>
                    <div className="px-4 py-3 border-r flex justify-center">
                      <button
                        onClick={() => handleEditUser(u)}
                        className="text-emerald-600 hover:underline flex items-center gap-1"
                      >
                        <Edit size={16} /> Edit
                      </button>
                    </div>
                    <div className="px-4 py-3 flex justify-center">
                      <button
                        onClick={() => handleDeleteUser(u)}
                        className="text-red-500 hover:underline flex items-center gap-1"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile */}
            <div className="md:hidden space-y-3">
              {adminUsers.map((u) => (
                <div
                  key={u.userUuid}
                  className="bg-white border rounded-xl p-4 shadow-sm relative"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">
                          {u.userFirstname} {u.userLastname}
                        </h3>
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">
                          Admin
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {u.userEmail || "-"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {u.userPhoneno || "-"} · {u.address || "-"}
                      </p>
                    </div>
                    <button onClick={() => toggleMenu(u.userUuid)}>
                      <MoreVertical />
                    </button>
                    {menuOpen === u.userUuid && (
                      <div className="absolute right-4 top-12 bg-white border rounded shadow w-32 z-50">
                        <button
                          onClick={() => handleEditUser(u)}
                          className="flex items-center gap-2 px-3 py-2 w-full"
                        >
                          <Edit size={16} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u)}
                          className="flex items-center gap-2 px-3 py-2 text-red-500 w-full"
                        >
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ================= CLIENT USERS ================= */}
      <section>
        <div className="mb-3 flex items-center gap-2">
          <h3 className="text-lg font-semibold text-slate-800">Users</h3>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
            {clientUsers.length}
          </span>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-gray-300 rounded-md overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-100 text-sm font-semibold text-gray-700">
            <div className="px-4 py-3 border-r">First Name</div>
            <div className="px-4 py-3 border-r">Last Name</div>
            <div className="px-4 py-3 border-r">Project Name</div>
            <div className="px-4 py-3 border-r">Device ID</div>
            <div className="px-4 py-3 border-r text-center">Edit</div>
            <div className="px-4 py-3 border-r text-center">Delete</div>
            <div className="px-4 py-3 text-center">View</div>
          </div>

          <div className="divide-y">
            {clientUsers.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No client users found.
              </div>
            ) : (
              clientUsers.map((u) => {
                const project = getUserProject(u);
                const deviceId = project?.devices?.[0]?.deviceId || "-";

                return (
                  <div
                    key={u.userUuid}
                    className="grid grid-cols-7 items-center bg-white"
                  >
                    <div className="px-4 py-3 border-r">
                      {u.userFirstname || "-"}
                    </div>
                    <div className="px-4 py-3 border-r">
                      {u.userLastname || "-"}
                    </div>
                    <div className="px-4 py-3 border-r">
                      {project?.projectName || "-"}
                    </div>
                    <div className="px-4 py-3 border-r text-sm break-all">
                      {deviceId}
                    </div>

                    <div className="px-4 py-3 border-r flex justify-center">
                      <button
                        onClick={() => handleEditUser(u)}
                        className="text-emerald-600 hover:underline flex items-center gap-1"
                      >
                        <Edit size={16} /> Edit
                      </button>
                    </div>

                    <div className="px-4 py-3 border-r flex justify-center">
                      <button
                        onClick={() => handleDeleteUser(u)}
                        className="text-red-500 hover:underline flex items-center gap-1"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>

                    <div className="px-4 py-3 flex justify-center">
                      <button
                        onClick={() =>
                          project?.projectUuid &&
                          navigate(
                            `/urjabio/admin/metrics/${project.projectUuid}`,
                          )
                        }
                        className="text-[#34C759] hover:underline flex items-center gap-1"
                      >
                        <Eye size={16} /> View
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-4">
          {clientUsers.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              No client users found.
            </div>
          ) : (
            clientUsers.map((u) => {
              const project = getUserProject(u);
              const deviceId = project?.devices?.[0]?.deviceId || "-";

              return (
                <div
                  key={u.userUuid}
                  className="bg-white border rounded-xl p-4 shadow-sm relative"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">
                        {u.userFirstname} {u.userLastname}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {project?.projectName || "-"}
                      </p>
                    </div>

                    <button onClick={() => toggleMenu(u.userUuid)}>
                      <MoreVertical />
                    </button>

                    {menuOpen === u.userUuid && (
                      <div className="absolute right-4 top-12 bg-white border rounded shadow w-32 z-50">
                        <button
                          onClick={() => handleEditUser(u)}
                          className="flex items-center gap-2 px-3 py-2 w-full"
                        >
                          <Edit size={16} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u)}
                          className="flex items-center gap-2 px-3 py-2 text-red-500 w-full"
                        >
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-xs text-gray-500 break-all">
                      Device: {deviceId}
                    </p>

                    <button
                      onClick={() =>
                        project?.projectUuid &&
                        navigate(
                          `/urjabio/admin/metrics/${project.projectUuid}`,
                        )
                      }
                      className="text-[#34C759] flex items-center gap-1"
                    >
                      <Eye size={16} /> View
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
};

export default AllUser;
